import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendar, 
  faUser, 
  faEye, 
  faArrowLeft,
  faShare,
  faHeart,
  faComment,
  faTag
} from '@fortawesome/free-solid-svg-icons';

const NewsDetail = () => {
  const { id } = useParams();
  const pageRef = useRef(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // 模拟新闻数据
  const newsData = {
    1: {
      id: 1,
      title: '云帐天下智能财务系统V3.0正式发布',
      summary: '全新升级的V3.0版本带来了更强大的AI功能和更友好的用户体验，助力物流企业数字化转型。',
      category: 'product',
      categoryName: '产品动态',
      author: '产品团队',
      date: '2024-01-15',
      views: 1250,
      likes: 89,
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      content: `
        <p>经过数月的精心研发和测试，云帐天下智能财务系统V3.0版本今日正式发布。这一全新版本在AI智能化、用户体验和系统性能等方面都有了显著提升，将为物流快运企业带来更加高效、智能的财务管理体验。</p>
        
        <h2>核心功能升级</h2>
        
        <h3>1. AI智能凭证生成</h3>
        <p>V3.0版本引入了全新的AI算法，能够更准确地识别和分类业务数据，自动生成符合会计准则的记账凭证。相比上一版本，凭证生成准确率提升至99.8%，处理速度提升300%。</p>
        
        <h3>2. 实时数据同步优化</h3>
        <p>新版本对数据同步机制进行了全面优化，支持更多第三方平台的数据接入，同步延迟降低至秒级，确保财务数据的实时性和准确性。</p>
        
        <h3>3. 智能财务分析</h3>
        <p>新增智能财务分析模块，通过机器学习算法分析历史数据，为企业提供经营趋势预测、成本优化建议等智能化服务。</p>
        
        <h2>用户体验提升</h2>
        
        <p>V3.0版本在用户界面设计上采用了全新的设计语言，界面更加简洁直观。同时优化了操作流程，减少了用户的学习成本，提升了工作效率。</p>
        
        <ul>
          <li>全新的响应式设计，完美适配各种设备</li>
          <li>优化的导航结构，操作更加便捷</li>
          <li>智能搜索功能，快速定位所需信息</li>
          <li>个性化工作台，支持自定义布局</li>
        </ul>
        
        <h2>系统性能优化</h2>
        
        <p>在系统架构方面，V3.0版本采用了微服务架构，提升了系统的稳定性和可扩展性。同时对数据库进行了优化，查询性能提升50%以上。</p>
        
        <h2>安全性增强</h2>
        
        <p>新版本在安全性方面也有重要提升，新增了多重身份验证、数据加密传输、操作日志审计等安全功能，确保客户数据的安全性。</p>
        
        <h2>客户反馈</h2>
        
        <p>"V3.0版本的AI功能真的很强大，大大减少了我们的工作量。"——某大型物流企业财务总监</p>
        
        <p>"新的界面设计很棒，操作起来更加顺手了。"——德邦物流用户</p>
        
        <h2>升级指南</h2>
        
        <p>现有用户可以通过系统内的升级提示进行在线升级，整个过程无需停机，不会影响日常业务操作。新用户可以直接注册使用V3.0版本。</p>
        
        <p>如需了解更多V3.0版本的功能特性，请联系我们的客服团队或访问产品页面。</p>
      `,
      tags: ['产品发布', 'AI技术', '系统升级', '用户体验']
    }
  };

  const relatedNews = [
    {
      id: 2,
      title: '物流行业数字化转型趋势分析报告',
      date: '2024-01-12',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      id: 3,
      title: '如何利用AI技术提升财务管理效率',
      date: '2024-01-10',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      id: 4,
      title: '云帐天下荣获"最佳财务科技创新奖"',
      date: '2024-01-08',
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  const currentNews = newsData[id];

  useEffect(() => {
    if (currentNews) {
      setLikeCount(currentNews.likes);
    }
    
    // 页面入场动画
    gsap.fromTo(
      pageRef.current.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out'
      }
    );
  }, [id, currentNews]);

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!currentNews) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">新闻不存在</h1>
          <Link to="/news" className="text-primary-blue hover:text-primary-dark">
            返回新闻列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="page-transition">
      {/* 返回按钮 */}
      <div className="bg-background-light py-4">
        <div className="container mx-auto px-4">
          <Link
            to="/news"
            className="inline-flex items-center text-primary-blue hover:text-primary-dark transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            返回新闻列表
          </Link>
        </div>
      </div>

      {/* 新闻头部 */}
      <section className="py-12 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* 分类标签 */}
            <div className="mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-blue text-white">
                <FontAwesomeIcon icon={faTag} className="mr-1" />
                {currentNews.categoryName}
              </span>
            </div>
            
            {/* 标题 */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {currentNews.title}
            </h1>
            
            {/* 摘要 */}
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {currentNews.summary}
            </p>
            
            {/* 元信息 */}
            <div className="flex flex-wrap items-center gap-6 text-gray-500 mb-8">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUser} className="mr-2" />
                {currentNews.author}
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                {formatDate(currentNews.date)}
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEye} className="mr-2" />
                {currentNews.views} 次阅读
              </div>
            </div>
            
            {/* 社交按钮 */}
            <div className="flex items-center gap-4 mb-8">
              <button
                onClick={handleLike}
                className={`flex items-center px-4 py-2 rounded-lg transition-all duration-300 ${
                  liked
                    ? 'bg-red-500 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FontAwesomeIcon icon={faHeart} className="mr-2" />
                {likeCount}
              </button>
              <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                <FontAwesomeIcon icon={faShare} className="mr-2" />
                分享
              </button>
              <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                <FontAwesomeIcon icon={faComment} className="mr-2" />
                评论
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 新闻内容 */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* 主图 */}
            <div className="mb-12">
              <img
                src={currentNews.image}
                alt={currentNews.title}
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
            
            {/* 文章内容 */}
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: currentNews.content }}
              style={{
                lineHeight: '1.8',
                fontSize: '18px'
              }}
            />
            
            {/* 标签 */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">相关标签</h3>
              <div className="flex flex-wrap gap-2">
                {currentNews.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 相关新闻 */}
      <section className="py-12 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">相关新闻</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedNews.map((news) => (
                <Link
                  key={news.id}
                  to={`/news/${news.id}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      {news.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatDate(news.date)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;